import { startCompletion } from "@codemirror/autocomplete";
import { syntaxTree } from "@codemirror/language";
import { EditorSelection, type EditorState, Transaction } from "@codemirror/state";
import type { EditorView } from "@codemirror/view";
import { notify, controlCharsRegEx, modifierCorrectIndentationMatch, modifierObject } from "@quickpoint.me/shared";

// matches the two-space unit the Tab key inserts (insertTab.ts) and the
// indentation the grammar expects for modifiers (modifierCorrectIndentationMatch)
const INDENT = "  ";

/**
 * Check if text formatting (bold/italic/underline) can be applied to the current selection.
 * Returns true if formatting can be applied, false otherwise.
 */
export function canApplyTextFormatting(state: EditorState): boolean {
  const selection = state.selection.main;

  // Need a non-empty selection to apply formatting
  if (selection.empty) return false;

  // Can't format across multiple lines
  const startLine = state.doc.lineAt(selection.from);
  const endLine = state.doc.lineAt(selection.to);
  if (startLine.number !== endLine.number) return false;

  const pos = selection.from;
  const node = syntaxTree(state).resolve(pos, 1);

  // Can't format keywords
  if (node.type.name === "Keyword") return false;

  // Can't format non-text elements
  if (node.type.name === "NotText") return false;

  // Can't format modifier lines
  if (modifierCorrectIndentationMatch.test(startLine.text)) return false;

  // Can't format control character lines
  if (controlCharsRegEx.test(startLine.text)) return false;

  return true;
}

export function insertWordAt({ word, state, view, newLine, insertAtLineNumber, isModifier }:
  {
    word: string,
    state: EditorState,
    view: EditorView,
    newLine: boolean | undefined,
    insertAtLineNumber: number | false,
    isModifier?: boolean,
  }
) {
  const pos = state.selection.ranges[0].from;
  const insertLine = insertAtLineNumber ? state.doc.line(insertAtLineNumber) : state.doc.lineAt(pos);
  const lineIsBlank = insertLine.text.trim() === "";

  // control characters (===, ---, -->, ```) go on a line of their own
  if (newLine) {
    const startLine = insertLine.text === "" ? "" : "\n";
    view.contentDOM.focus();
    view.dispatch({
      changes: { from: insertLine.to, insert: `${startLine}${word}\n` },
      selection: { anchor: insertLine.to + word.length + (insertLine.text === "" ? 1 : 2) },
    });
    startCompletion(view);
    return;
  }

  // YAML-like structure: keywords sit at column 0, modifiers one indent
  // under their keyword
  const indent = isModifier ?? word in modifierObject;
  const text = `${indent ? INDENT : ""}${word} `;

  const insertCode = lineIsBlank
    ? {
      // reuse the blank line the cursor is on — replacing the whole line
      // also swallows any indentation the user already typed
      changes: { from: insertLine.from, to: insertLine.to, insert: text },
      selection: { anchor: insertLine.from + text.length },
    }
    : {
      // line has content: insert on a new line directly below it
      changes: { from: insertLine.to, insert: `\n${text}` },
      selection: { anchor: insertLine.to + 1 + text.length },
    };

  view.contentDOM.focus();
  view.dispatch(insertCode);
  startCompletion(view);
}

export function insertCharacterAtCursor(
  character: string,
  state: EditorState,
  view: EditorView,
  autocomplete = false,
) {
  const pos = state.selection.ranges[0].from;
  // console.log("insertCharacterAtCursor", autocomplete, Transaction.userEvent.of("input"));

  const insertCode = {
    changes: {
      from: pos,
      insert: character,

    },
    selection: {
      anchor: pos + 1,
    },
    annotations: autocomplete ? Transaction.userEvent.of("input.type") : undefined, // Simulate user input


  };
  view.contentDOM.focus();
  view.dispatch(insertCode);
}

export function wrapSelection(textModifier: string, view: EditorView) {
  const selection = view.state.selection.ranges[0];
  const pos = selection.from;

  // existing syntaxTree / validation checks (leave them as they are)
  const node = syntaxTree(view.state).resolve(pos, 1);

  if (node.type.name === "Keyword") {
    notify("Only select the text you wish to affect", { variant: "info" });
    return;
  }

  if (node.type.name === "NotText") {
    notify("Only text, not keywords or modifiers can be decorated.", { variant: "info" });
    return;
  }

  const leftSideOfSelection = selection.from;

  const line = view.state.doc.lineAt(leftSideOfSelection);

  if (modifierCorrectIndentationMatch.test(line.text)) {
    notify("You can only apply this to text, not to  modifiers. Select some text and try again", { variant: "info" });
    return;
  }
  if (controlCharsRegEx.test(line.text)) {
    notify("You can only apply this to text, not to  modifiers. Select some text and try again", { variant: "info" });
    return;
  }

  // ----------  NEW  ----------
  // If nothing is selected, try to remove an existing decoration pair.
  if (selection.empty) {
    const doc = view.state.doc;
    const modifierLen = textModifier.length;

    const beforeIdx = doc.sliceString(0, pos).lastIndexOf(textModifier);
    const afterRelative = doc.sliceString(pos).indexOf(textModifier);
    const afterIdx = afterRelative === -1 ? -1 : pos + afterRelative;

    const betweenText =
      beforeIdx !== -1 && afterIdx !== -1
        ? doc.sliceString(beforeIdx + modifierLen, afterIdx)
        : "";

    // inside a pair & pair does not cross a newline
    if (
      beforeIdx !== -1 &&
      afterIdx !== -1 &&
      beforeIdx < pos &&
      afterIdx >= pos &&
      !betweenText.includes("\n")
    ) {
      view.contentDOM.focus();
      view.dispatch({
        changes: [
          { from: afterIdx, to: afterIdx + modifierLen, insert: "" },
          { from: beforeIdx, to: beforeIdx + modifierLen, insert: "" },
        ],
        selection: { anchor: pos - modifierLen },
      });
      startCompletion(view);
      return; // done – don’t fall through to the wrap-logic
    }
    // cursor not inside a decorated word – fall through to normal wrap logic
  }
  // ----------  END NEW ----------

  // ----------  NEW (non-empty selection toggle) ----------
  if (!selection.empty) {
    const doc = view.state.doc;
    const modifierLen = textModifier.length;

    // Check whether the selection is already wrapped in the modifier
    const hasPrefix =
      selection.from >= modifierLen &&
      doc.sliceString(selection.from - modifierLen, selection.from) ===
      textModifier;
    const hasSuffix =
      doc.sliceString(selection.to, selection.to + modifierLen) ===
      textModifier;

    if (hasPrefix && hasSuffix) {
      view.contentDOM.focus();
      view.dispatch({
        changes: [
          // remove left modifier
          { from: selection.from - modifierLen, to: selection.from, insert: "" },
          // remove right modifier (index still refers to original doc,
          // CodeMirror adjusts later changes automatically)
          { from: selection.to, to: selection.to + modifierLen, insert: "" },
        ],
        // keep original text selected after shrink
        selection: EditorSelection.range(
          selection.from - modifierLen,
          selection.to - modifierLen,
        ),
      });
      startCompletion(view);
      return; // stop – don’t add another pair
    }
  }
  // ----------  END NEW ----------

  // existing “wrap the (non-empty) selection” logic remains unchanged
  const insertCode = view.state.changeByRange(range => ({
    changes: [
      { from: range.from, insert: textModifier },
      { from: range.to, insert: textModifier },
    ],
    range: EditorSelection.range(
      range.from + textModifier.length,
      range.to + textModifier.length,
    ),
  }));

  view.contentDOM.focus();
  view.dispatch(insertCode);
  startCompletion(view);
}
